<template>
  <div class="pc__image-swiper" ref="imageCarouselRef" v-if="enableImageCarousel">
    <div class="pc__slide-wrapper">
      <div class="pc__slide" v-for="(image, index) in featureImageVariants" :key="index">
        <transition name="fade" mode="out-in">
          <ImageField
            class="pc__feature-img"
            :obj="{ url: image.src, alt: image.alt, width: 600, height: 750 }"
            :isLoadingLazy="isLoadingLazy"
            :key="image.src"
          />
        </transition>
      </div>
    </div>
    <div class="swiper-pagination"></div>
  </div>

  <template v-else>
    <transition name="fade" mode="out-in">
      <ImageField
        class="pc__feature-img"
        :obj="{ url: featureImage.src, alt: product.title, width: 600, height: 750 }"
        :isLoadingLazy="isLoadingLazy"
        :key="featureImage.src"
      />
    </transition>
    <ImageField
      :key="hoverImage ? hoverImage.src : featureImage.src"
      class="pc__hover-img"
      :obj="{ url: hoverImage ? hoverImage.src : featureImage.src, alt: product.title, width: 600, height: 750 }"
      :isLoadingLazy="isLoadingLazy"
    />
  </template>
</template>

<script setup>
  import { ref, computed, onMounted, watch, toRefs } from 'vue';
  import Swiper from 'swiper';
  import SwiperCore, { Navigation, EffectFade, Thumbs, Pagination, A11y } from 'swiper/core';
  import ImageField from '../image.vue';

  SwiperCore.use([Navigation, EffectFade, Thumbs, Pagination, A11y]);

  const props = defineProps({
    enableImageCarousel: {
      type: Boolean,
      default: false,
    },
    selectedColor: {
      type: String,
      default: '',
    },
    useLifestyleImage: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
    },
    isLoadingLazy: {
      type: Boolean,
      default: false,
    },
  });

  const { enableImageCarousel, product, isLoadingLazy, selectedColor, useLifestyleImage } = toRefs(props);

  const imageCarouselRef = ref(null);
  const imageSwiper = ref(null);

  onMounted(() => {
    if (enableImageCarousel.value) {
      initializeSwiper();
    }
  });

  watch(enableImageCarousel, (newVal) => {
    if (newVal) {
      initializeSwiper();
    } else if (imageSwiper.value) {
      imageSwiper.value.destroy();
    }
  });

  const featureImageVariants = computed(() => {
    const { images, title } = product.value;

    return window.getProductImages(images, selectedColor.value, title).slice(0, 4);
  });

  const featureImage = computed(() => {
    const { images, title } = product.value;

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];
    const lifestyleImage = imageData.find((image) => image.alt && image.alt.toLowerCase().includes('alternative'));

    if (useLifestyleImage.value && lifestyleImage) {
      return lifestyleImage;
    }

    return defaultImage;
  });

  const hoverImage = computed(() => {
    const { images, title } = product.value;

    const imageData = window.getProductImages(images, selectedColor.value, title);
    const defaultImage = imageData.find((image) => !image.alt || !image.alt.toLowerCase().includes('alternative')) || imageData[0];
    const filteredImageData = imageData.filter((image) => image.alt && image.alt.toLowerCase().includes('alternative'));
    const defaultHoverImage = filteredImageData[0];

    if (useLifestyleImage.value) {
      return defaultImage;
    }

    return defaultHoverImage;
  });

  const initializeSwiper = () => {
    imageSwiper.value = new Swiper(imageCarouselRef.value, {
      slidesPerView: 1,
      wrapperClass: 'pc__slide-wrapper',
      slideClass: 'pc__slide',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      a11y: {
        enabled: true,
      },
    });
  };
</script>
